import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Error Boundary Komponente für graceful error handling
 * Fängt JavaScript-Fehler in Child-Komponenten ab und zeigt
 * eine benutzerfreundliche Fehlermeldung an.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    // Optional: Send error to logging service
    if (process.env.NODE_ENV === 'production') {
      // TODO: Implement error logging service (e.g., Sentry)
      // logErrorToService(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback, showDetails = false } = this.props;

      // Custom fallback UI if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <Card className="max-w-lg w-full border-red-200 bg-red-50">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-red-800">
                Ein Fehler ist aufgetreten
              </CardTitle>
              <CardDescription className="text-red-600">
                Die Anwendung konnte diese Ansicht nicht laden.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Error details for development */}
              {showDetails && this.state.error && (
                <div className="p-3 bg-red-100 rounded-lg text-sm font-mono text-red-800 overflow-auto max-h-32">
                  {this.state.error.toString()}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={this.handleRetry}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Erneut versuchen
                </Button>
                <Button
                  onClick={this.handleReload}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Seite neu laden
                </Button>
              </div>

              {/* Help text */}
              <p className="text-xs text-center text-red-500 mt-4">
                Falls das Problem weiterhin besteht, kontaktieren Sie den Support.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
